import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Skeleton,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { AutoAwesome, Info } from 'hephaestus/webIcons';
import {
  createContext,
  type MouseEvent,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useRebalanceInsightNarrative } from '../../hooks/useRebalanceInsightNarrative';
import type { GenerateRebalanceInsightNarrativeInput } from '../../types/insightNarrative.types';

type InsightAiScope = 'portfolio' | 'run';

/** Kill switch if Gemini is still misconfigured after the 3.5 model deploy. */
const INSIGHT_AI_GENERATE_ENABLED = true;

type InsightAiContextValue = {
  scope: InsightAiScope;
  disabled: boolean;
  loading: boolean;
  narrative: string | null;
  errorMessage: string | null;
  called: boolean;
  onGenerate: (event?: MouseEvent) => void;
};

const InsightAiContext = createContext<InsightAiContextValue | null>(null);

const useInsightAiContext = () => {
  const value = useContext(InsightAiContext);
  if (!value) {
    throw new Error(
      'Insight AI controls must render inside BulkRebalanceInsightAiNarrative',
    );
  }

  return value;
};

export type BulkRebalanceInsightAiNarrativeProps = {
  scope: InsightAiScope;
  input: GenerateRebalanceInsightNarrativeInput;
  disabled?: boolean;
  onGenerateStart?: () => void;
  children: ReactNode;
};

const stopAccordionToggle = (event: MouseEvent) => {
  event.stopPropagation();
};

const insightAiCardSx = (theme: ReturnType<typeof useTheme>) => ({
  p: 1.25,
  borderRadius: theme.borderRadius.sm,
  border: `1px solid ${theme.color.neutralPressed}`,
  bgcolor: theme.color.surfaceContainer,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
});

const insightAiMarkdownSx = {
  '& p': { m: 0, mb: 1 },
  '& p:last-child': { mb: 0 },
  '& ul': {
    m: 0,
    p: 0,
    pl: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
    listStyleType: 'disc',
  },
  '& li': {
    mb: 0,
    pl: 0.25,
  },
  '& li::marker': {
    color: 'text.secondary',
  },
  '& strong': { fontWeight: 600 },
};

type InsightAiNarrativeShellProps = {
  testId: string;
  title?: string;
  children: ReactNode;
};

const InsightAiNarrativeShell = ({
  testId,
  title,
  children,
}: InsightAiNarrativeShellProps) => {
  const theme = useTheme();

  return (
    <Box data-testid={testId} sx={insightAiCardSx(theme)}>
      {title ? (
        <Typography
          variant='labelSmall'
          color='textSecondary'
          sx={{ fontWeight: 600 }}
        >
          {title}
        </Typography>
      ) : null}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Box
          sx={{
            mt: 0.125,
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: theme.color.surface,
            border: `1px solid ${theme.color.outlineVariant}`,
            color: theme.palette.supportOne50,
          }}
        >
          <AutoAwesome sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      </Box>
    </Box>
  );
};

export const BulkRebalanceInsightAiNarrative = ({
  scope,
  input,
  disabled = false,
  onGenerateStart,
  children,
}: BulkRebalanceInsightAiNarrativeProps) => {
  const { generate, narrative, loading, error, called } =
    useRebalanceInsightNarrative(input);

  const value = useMemo<InsightAiContextValue>(
    () => ({
      scope,
      disabled,
      loading,
      narrative,
      errorMessage: error?.message ?? null,
      called,
      onGenerate: (event?: MouseEvent) => {
        event?.stopPropagation();
        if (!INSIGHT_AI_GENERATE_ENABLED || disabled || loading) {
          return;
        }

        onGenerateStart?.();
        void generate(input);
      },
    }),
    [
      called,
      disabled,
      error?.message,
      generate,
      input,
      loading,
      narrative,
      onGenerateStart,
      scope,
    ],
  );

  return (
    <InsightAiContext.Provider value={value}>
      {children}
    </InsightAiContext.Provider>
  );
};

export const BulkRebalanceInsightAiGenerateButton = () => {
  const { t } = useTranslation(['bulkRebalance']);
  const theme = useTheme();
  const { scope, disabled, loading, narrative, onGenerate } =
    useInsightAiContext();
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  return (
    <>
      <Box
        data-testid={`bulk-rebalance-insight-ai-generate-${scope}`}
        onClick={stopAccordionToggle}
        sx={{
          display: 'inline-flex',
          alignItems: 'stretch',
          flexShrink: 0,
          borderRadius: theme.borderRadius.sm,
          overflow: 'hidden',
        }}
      >
        <Button
          variant='contained'
          color='secondary'
          size='small'
          disabled={!INSIGHT_AI_GENERATE_ENABLED || disabled || loading}
          loading={loading}
          startIcon={<AutoAwesome sx={{ fontSize: '1rem !important' }} />}
          onClick={onGenerate}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            px: 1.25,
            py: 0.5,
            minWidth: 0,
            borderRadius: 0,
            whiteSpace: 'nowrap',
            boxShadow: 'none',
          }}
        >
          {narrative
            ? t('insights.ai.regenerate')
            : t('insights.ai.generateShort')}
          <Typography
            component='span'
            variant='labelSmall'
            sx={{
              ml: 0.75,
              opacity: 0.85,
              fontWeight: 700,
              letterSpacing: '0.06em',
            }}
          >
            {t('insights.ai.betaLabel')}
          </Typography>
        </Button>
        <IconButton
          aria-label={t('insights.ai.disclaimerTitle')}
          size='small'
          onClick={(event) => {
            event.stopPropagation();
            setDisclaimerOpen(true);
          }}
          data-testid={`bulk-rebalance-insight-ai-info-${scope}`}
          sx={{
            borderRadius: 0,
            px: 0.75,
            boxShadow: 'none',
            borderLeft: '1px solid rgba(255, 255, 255, 0.24)',
          }}
        >
          <Info fontSize='small' />
        </IconButton>
      </Box>
      <Dialog open={disclaimerOpen} onClose={() => setDisclaimerOpen(false)}>
        <DialogTitle onClose={() => setDisclaimerOpen(false)}>
          {t('insights.ai.disclaimerTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant='bodyMedium'>
            {t('insights.ai.disclaimer')}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const BulkRebalanceInsightAiResult = () => {
  const { t } = useTranslation(['bulkRebalance']);
  const { scope, loading, narrative, errorMessage, called } =
    useInsightAiContext();
  const notesTitle = t(`insights.ai.${scope}.title`);

  if (loading) {
    return (
      <InsightAiNarrativeShell
        testId={`bulk-rebalance-insight-ai-loading-${scope}`}
        title={notesTitle}
      >
        <Typography variant='labelSmall' color='textSecondary'>
          {t('insights.ai.loading')}
        </Typography>
        <Skeleton variant='text' width='100%' />
        <Skeleton variant='text' width='92%' />
        <Skeleton variant='text' width='80%' />
      </InsightAiNarrativeShell>
    );
  }

  if (errorMessage && called) {
    return (
      <InsightAiNarrativeShell
        testId={`bulk-rebalance-insight-ai-error-${scope}`}
        title={notesTitle}
      >
        <Alert severity='info' sx={{ m: 0 }}>
          {errorMessage || t('insights.ai.error')}
        </Alert>
      </InsightAiNarrativeShell>
    );
  }

  if (!narrative) {
    return null;
  }

  return (
    <InsightAiNarrativeShell
      testId={`bulk-rebalance-insight-ai-narrative-${scope}`}
      title={notesTitle}
    >
      <Typography
        variant='bodySmall'
        component='div'
        color='textPrimary'
        sx={insightAiMarkdownSx}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{narrative}</ReactMarkdown>
      </Typography>
    </InsightAiNarrativeShell>
  );
};
